/**
 * Hook for package management operations
 */
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectShipmentPackages, selectPackageContentBySeqId, selectPackageRouteSegments, selectPackageMetadataBySeqId, selectBoxTypes } from '../../redux/packing';
import { usePackingActions } from './usePackingActions';
export var usePackageManagement = function () {
    var boxTypes = useSelector(selectBoxTypes);
    var packages = useSelector(selectShipmentPackages);
    var packageContentBySeqId = useSelector(selectPackageContentBySeqId);
    var packageRouteSegments = useSelector(selectPackageRouteSegments);
    var packageMetadataBySeqId = useSelector(selectPackageMetadataBySeqId);
    var _a = usePackingActions(), handleClosePackage = _a.handleClosePackage, handleDeletePackage = _a.handleDeletePackage, handleResetPackage = _a.handleResetPackage, handleUpdatePackage = _a.handleUpdatePackage;
    var getBoxTypeName = useCallback(function (boxTypeId) {
        var _a;
        return (_a = boxTypes.find(function (boxType) { return boxType.key === boxTypeId; })) === null || _a === void 0 ? void 0 : _a.value;
    }, [boxTypes]);
    // Get info for a specific package
    var getPackageInfo = useCallback(function (seqId) {
        var _a;
        // First try packageMetadataBySeqId, fall back to packageContentBySeqId
        var pkgFromMetadata = (_a = packageMetadataBySeqId[seqId]) === null || _a === void 0 ? void 0 : _a[0];
        var pkg = pkgFromMetadata || packages.find(function (p) { return p.shipmentPackageSeqId === seqId; });
        if (!pkg)
            return null;
        var contents = packageContentBySeqId[seqId] || [];
        var routeSegment = packageRouteSegments.find(function (rs) { return rs.shipmentPackageSeqId === seqId; });
        return {
            shipmentPackageSeqId: seqId,
            shipmentBoxTypeId: pkg.shipmentBoxTypeId,
            boxTypeDescription: pkg.boxTypeDescription,
            weight: pkg.weight,
            weightUomId: pkg.weightUomId,
            isClosed: !!pkg.shipmentBoxTypeId && !!pkg.weight,
            hasLabel: !!(routeSegment === null || routeSegment === void 0 ? void 0 : routeSegment.labelUrl),
            hasReturnLabel: !!(routeSegment === null || routeSegment === void 0 ? void 0 : routeSegment.returnLabelUrl),
            itemCount: contents.length,
            trackingNumber: routeSegment === null || routeSegment === void 0 ? void 0 : routeSegment.trackingCode
        };
    }, [packages, packageMetadataBySeqId, packageContentBySeqId, packageRouteSegments]);
    // Get info for all packages
    var getAllPackagesInfo = useCallback(function () {
        return packages.map(function (pkg) { return getPackageInfo(pkg.shipmentPackageSeqId); }).filter(Boolean);
    }, [packages, getPackageInfo]);
    // Check if there are open packages
    var hasOpenPackages = useCallback(function () {
        return packages.some(function (pkg) { return !pkg.shipmentBoxTypeId || !pkg.weight; });
    }, [packages]);
    // Check if there are closed packages
    var hasClosedPackages = useCallback(function () {
        return packages.some(function (pkg) { return !!pkg.shipmentBoxTypeId && !!pkg.weight; });
    }, [packages]);
    return {
        packages: packages,
        packageContentBySeqId: packageContentBySeqId,
        packageMetadataBySeqId: packageMetadataBySeqId,
        getBoxTypeName: getBoxTypeName,
        getPackageInfo: getPackageInfo,
        getAllPackagesInfo: getAllPackagesInfo,
        hasOpenPackages: hasOpenPackages,
        hasClosedPackages: hasClosedPackages,
        closePackage: handleClosePackage,
        deletePackage: handleDeletePackage,
        resetPackage: handleResetPackage,
        updatePackage: handleUpdatePackage
    };
};
export default usePackageManagement;
